<template>
  <div class="">
    <header class="py-10">
      <div class="md:flex md:items-center md:justify-between max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex-1 min-w-0">
          <h2 class="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:leading-9 sm:truncate">
            Dataload from <TimeAgo :time="studentDataload.insertedAt"/>
          </h2>
        </div>
      </div>
    </header>

    <main class="">
      <div class="max-w-7xl mx-auto pb-12 px-4 sm:px-6 lg:px-8">
        <div class="bg-white rounded-lg shadow border border-cool-gray-200 px-5 py-6 sm:px-6">
          <Mutation
            :mutation="mutation"
            :variables="variables"
            @done="onDone"
            @error="onError"
            tag="form"
            @submit.prevent="mutate"
          >
            <template v-slot="{mutate, loading}">
              <ValidationObserver
                ref="observer"
                v-slot="{ valid, invalid }"
                tag="form"
                @submit.prevent="mutate"
              >
                <div>
                  <div> <!-- Beginning of Worksheet Selection -->
                    <div>
                      <h3 class="text-lg leading-6 font-medium text-gray-900">
                        Worksheet Selection
                      </h3>
                      <p class="mt-1 text-sm leading-5 text-gray-500">
                        Which page of your spreadsheet contains your student data?
                      </p>
                    </div>
                    <div class="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                      <div class="sm:col-span-4">
                        <fieldset class="">
                          <div class="mt-4">
                            <div class="relative flex items-start" v-for="(worksheet, index) in studentDataload.data" :key="index">
                              <div class="flex items-center h-5">
                              <input
                                v-model="worksheetIndex"
                                :value="index"
                                id="push_everything"
                                name="form-input push_notifications"
                                type="radio"
                                class="form-radio h-4 w-4 text-indigo-600 transition duration-150 ease-in-out">
                              </div>
                              <div class="ml-3 text-sm leading-5">
                                <label for="offers" class="font-medium text-gray-700">Worksheet {{index + 1}}</label>
                                <p class="text-gray-500">{{studentDataload.data[index].length}} rows of data</p>
                              </div>
                            </div>
                          </div>
                        </fieldset>

                        <fieldset>
                          <div class="mt-4">
                            <div class="relative flex items-start">
                              <div class="flex items-center h-5">
                                <input
                                  id="comments"
                                  type="checkbox"
                                  v-model="hasHeaderRow"
                                  class="form-checkbox h-4 w-4 text-indigo-600 transition duration-150 ease-in-out">
                              </div>
                              <div class="ml-3 text-sm leading-5">
                                <label for="comments" class="font-medium text-gray-700">This worksheet contains a header row.</label>
                                <p class="text-gray-500">The first row of the selected worksheet is: {{studentDataload.data[worksheetIndex][0]}}</p>
                              </div>
                            </div>
                          </div>
                        </fieldset>
                      </div>
                    </div>
                  </div> <!-- End of Worksheet Selection -->

                  <div class="mt-8 border-t border-gray-200 pt-8"> <!-- Beginning of Column Mapping -->
                    <div>
                      <h3 class="text-lg leading-6 font-medium text-gray-900">
                        Column Matching
                      </h3>
                      <p class="mt-1 text-sm leading-5 text-gray-500">
                        Which columns in your worksheet match to the given columns?
                      </p>
                    </div>
                    <div v-for="n in (numberOfColumnsForSelectedWorksheet)" :key="n" class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                      <div class="sm:col-span-1">
                        <label for="last_name" class="block text-sm font-medium leading-5 text-gray-700">
                          Column {{n}}
                        </label>
                      </div>
                      <div class="sm:col-span-3">
                        <label for="last_name" class="block text-sm font-medium leading-5 text-gray-700">
                          {{getFirstFiveColumnValues(n-1)}}
                        </label>
                      </div>
                      <div class="sm:col-span-2">
                        <div class="mt-1 rounded-md shadow-sm">
                          <select :value="reverseColumnMapping[n-1]" @change="setReverseColumnMapping(n-1, $event.target.value)" class="mt-1 block form-select w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue focus:border-blue-300 transition duration-150 ease-in-out sm:text-sm sm:leading-5">
                            <option :value="undefined">Ignore this column</option>
                            <option :value="undefined"></option>
                            <option :value="'schoolIdentifier'">Student ID Number</option>
                            <option :value="'first'">First</option>
                            <option :value="'last'">Last</option>
                            <option :value="undefined"></option>
                            <option :value="'firstLast'">First Last (full student name)</option>
                            <option :value="'lastCommaFirst'">Last Comma First (full student name)</option>
                            <option :value="undefined"></option>
                            <option :value="'gradeLevel'">Grade Level</option>
                            <option :value="'homeLanguage'">Home Language</option>
                            <option :value="undefined"></option>
                            <option :value="'number1'">Home Number 1</option>
                            <option :value="'number1Label'">Home Number 1 Label</option>
                            <option :value="undefined"></option>
                            <option :value="'number2'">Home Number 2</option>
                            <option :value="'number2Label'">Home Number 2 Label</option>
                            <option :value="undefined"></option>
                            <option :value="'number3'">Home Number 3</option>
                            <option :value="'number3Label'">Home Number 3 Label</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  </div> <!-- End of Column Mapping -->

                  <div class="mt-8 border-t border-gray-200 pt-8"> <!-- Beginning of Grade Level Mapping -->
                    <div>
                      <h3 class="text-lg leading-6 font-medium text-gray-900">
                        Grade Level Matching
                      </h3>
                      <p class="mt-1 text-sm leading-5 text-gray-500">
                        Which of the Grade Levels you listed match to existing Grade Levels already in Cardstock?
                      </p>
                    </div>
                    <div v-if="!columnMapping.gradeLevel">
                      <div class="mt-6 rounded-md bg-yellow-50 p-4">
                        <div class="flex">
                          <div class="flex-shrink-0">
                            <svg class="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                              <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
                            </svg>
                          </div>
                          <div class="ml-3">
                            <h3 class="text-sm leading-5 font-medium text-yellow-800">
                              No Grade Level Column Selected
                            </h3>
                            <div class="mt-2 text-sm leading-5 text-yellow-700">
                              <p>
                                To match Grade Level data, choose a column above that contains Grade Level information.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div v-else-if="possibleGradeLevels.length == 0">
                      <div class="mt-6 rounded-md bg-yellow-50 p-4">
                        <div class="flex">
                          <div class="flex-shrink-0">
                            <svg class="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                              <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
                            </svg>
                          </div>
                          <div class="ml-3">
                            <h3 class="text-sm leading-5 font-medium text-yellow-800">
                              Empty Grade Level Column Selected
                            </h3>
                            <div class="mt-2 text-sm leading-5 text-yellow-700">
                              <p>
                                The column you selected for Grade Level above does not have any data in it, so we have no Grade Level data to match on.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div v-else>
                      <div v-for="n in possibleGradeLevels" :key="n" class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                        <div class="sm:col-span-3">
                          <label for="last_name" class="block text-sm font-medium leading-5 text-gray-700">
                            {{n}}
                          </label>
                        </div>
                        <div class="sm:col-span-3">
                          <div class="mt-1 rounded-md shadow-sm">
                            <select :value="gradeLevelMappings[n]" @change="setGradeLevelMapping(n, $event.target.value)" class="mt-1 block form-select w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue focus:border-blue-300 transition duration-150 ease-in-out sm:text-sm sm:leading-5">
                              <option :value="undefined">Ignore this entry</option>
                              <option :value="null">Create a new grade level called {{n}}</option>
                              <option :value="gradeLevel.id" v-for="gradeLevel in gradeLevels" :key="gradeLevel.id">
                                Match as existing {{gradeLevel.name}}
                              </option>
                            </select>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div> <!-- End of Grade Level Mapping -->

                  <div class="mt-8 border-t border-gray-200 pt-8"> <!-- Beginning of HomeLanguage Mapping -->
                    <div>
                      <h3 class="text-lg leading-6 font-medium text-gray-900">
                        Home Language Matching
                      </h3>
                      <p class="mt-1 text-sm leading-5 text-gray-500">
                        Which of the Home Languages you listed match to language Cardstock can translate?
                      </p>
                    </div>
                    <div v-if="!columnMapping.homeLanguage">
                      <div class="mt-6 rounded-md bg-yellow-50 p-4">
                        <div class="flex">
                          <div class="flex-shrink-0">
                            <svg class="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                              <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
                            </svg>
                          </div>
                          <div class="ml-3">
                            <h3 class="text-sm leading-5 font-medium text-yellow-800">
                              No Home Language Column Selected
                            </h3>
                            <div class="mt-2 text-sm leading-5 text-yellow-700">
                              <p>
                                To match Home Language data, choose a column above that contains Home Language information.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div v-else-if="possibleHomeLanguages.length == 0">
                      <div class="mt-6 rounded-md bg-yellow-50 p-4">
                        <div class="flex">
                          <div class="flex-shrink-0">
                            <svg class="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                              <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
                            </svg>
                          </div>
                          <div class="ml-3">
                            <h3 class="text-sm leading-5 font-medium text-yellow-800">
                              Empty Home Language Column Selected
                            </h3>
                            <div class="mt-2 text-sm leading-5 text-yellow-700">
                              <p>
                                The column you selected for Home Languages above does not have any data in it, so we have no Home Language data to match on.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div v-else>
                      <div v-for="n in possibleHomeLanguages" :key="n" class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                        <div class="sm:col-span-3">
                          <label for="last_name" class="block text-sm font-medium leading-5 text-gray-700">
                            {{n}}
                          </label>
                        </div>
                        <div class="sm:col-span-3">
                          <div class="mt-1 rounded-md shadow-sm">
                            <select :value="homeLanguageMappings[n]" @change="setHomeLanguageMapping(n, $event.target.value)" class="mt-1 block form-select w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue focus:border-blue-300 transition duration-150 ease-in-out sm:text-sm sm:leading-5">
                              <option :value="undefined">Ignore this entry</option>
                              <option :value="language.id" v-for="language in homeLanguages" :key="language.id">
                                Match as {{language.name}}
                              </option>
                            </select>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div> <!-- End of HomeLanguage Mapping -->

                </div>
                <div class="mt-8 border-t border-gray-200 pt-5">
                  <div class="flex justify-end">
                    <span class="inline-flex rounded-md shadow-sm">
                      <button type="button" class="py-2 px-4 border border-gray-300 rounded-md text-sm leading-5 font-medium text-gray-700 hover:text-gray-500 focus:outline-none focus:border-blue-300 focus:ring-blue active:bg-gray-50 active:text-gray-800 transition duration-150 ease-in-out">
                        Cancel
                      </button>
                    </span>
                    <span class="ml-3 inline-flex rounded-md shadow-sm">
                      <button type="submit" class="inline-flex justify-center py-2 px-4 border border-transparent text-sm leading-5 font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-500 focus:outline-none focus:border-indigo-700 focus:ring-indigo active:bg-indigo-700 transition duration-150 ease-in-out">
                        Save
                      </button>
                    </span>
                  </div>
                </div>

              </ValidationObserver>
            </template>
          </Mutation>

        </div>

      </div>
    </main>

  </div>
</template>
<script>
import StudentDataloadQuery from '@/queries/StudentDataload.gql'
import GradeLevelsQuery from '@/queries/GradeLevels.gql'
import HomeLanguagesQuery           from '@/queries/HomeLanguages.gql'
import { preloadMany } from '@/preloader'
import {
  ValidationObserver,
  ValidationProvider
} from "vee-validate"


import Mutation         from '@/components/Mutation.vue'
import TimeAgo         from '@/components/TimeAgo.vue'
import StudentDataloadProcessMutation from '@/mutations/StudentDataloadProcess.gql'

import _map from 'lodash-es/map'
import _max from 'lodash-es/max'
import _size from 'lodash-es/size'
import _take from 'lodash-es/take'
import _uniq from 'lodash-es/uniq'
import _findKey from 'lodash-es/findKey'
import _filter from 'lodash-es/filter'
import _omit from 'lodash-es/omit'

const preloadQueries = (to) => {
  return [
    {
      query: GradeLevelsQuery,
      queryName: 'gradeLevels',
      variables: {
        teamId: to.params.teamId
      },
      fetchPolicy: 'network-only'
    },
    {
      query: StudentDataloadQuery,
      queryName: "studentDataload",
      variables: {
        id: to.params.id
      },
      fetchPolicy: 'network-only'
    },
    {
      query: HomeLanguagesQuery,
      queryName: "homeLanguages",
      fetchPolicy: 'cache-first'
    }
  ]
}

export default {
  components: {
    Mutation,
    TimeAgo,
    ValidationObserver,
    ValidationProvider,
  },
  props: {
    id: { required: true },
    teamId: { required: true},
  },

  data() {
    return {
      reverseColumnMapping: {},
      gradeLevelMappings: {},
      homeLanguageMappings: {},
      worksheetIndex: 0,
      hasHeaderRow: true,
      mutation: StudentDataloadProcessMutation,
    }
  },

  computed: {
    columnMapping() {
      return {
        'first':            parseInt(this.getColumnMappingValue('first')),
        'schoolIdentifier': parseInt(this.getColumnMappingValue('schoolIdentifier')),
        'last':             parseInt(this.getColumnMappingValue('last')),
        'firstLast':        parseInt(this.getColumnMappingValue('firstLast')),
        'lastCommaFirst':   parseInt(this.getColumnMappingValue('lastCommaFirst')),
        'gradeLevel':       parseInt(this.getColumnMappingValue('gradeLevel')),
        'homeLanguage':     parseInt(this.getColumnMappingValue('homeLanguage')),
        'number_1':         parseInt(this.getColumnMappingValue('number1')),
        'number_1_label':   parseInt(this.getColumnMappingValue('number1Label')),
        'number_2':         parseInt(this.getColumnMappingValue('number2')),
        'number_2_label':   parseInt(this.getColumnMappingValue('number2Label')),
        'number_3':         parseInt(this.getColumnMappingValue('number3')),
        'number_3_label':   parseInt(this.getColumnMappingValue('number3Label')),
      }
    },

    finalGradeLevelMappings() {
      return _map(this.gradeLevelMappings, (value, key) => {
        return {
          grade_level_id: value,
          name: key
        }
      })
    },

    finalHomeLanguageMappings() {
      return _map(this.homeLanguageMappings, (value, key) => {
        return {
          home_language_id: value,
          name: key
        }
      })
    },

    variables() {
      return {
        id: this.id,
        columnMapping: this.columnMapping,
        gradeLevelMappings: this.finalGradeLevelMappings,
        homeLanguageMappings: this.finalHomeLanguageMappings,
        worksheetIndex: this.worksheetIndex,
        rowRejections: this.rowRejections,
      }
    },
    numberOfColumnsForSelectedWorksheet() {
      return _max(_map(this.selectedWorksheet, (row) => {
        return _size(row)
      }))
    },
    possibleGradeLevels() {
      var rawGradeLevelColumn = _map(this.selectedWorksheet, (row) => {
        return row[this.columnMapping.gradeLevel]
      })
      var uniques = _uniq(rawGradeLevelColumn)
      var nonEmpties = _filter(uniques, (gradeLevelCell) => {
        return gradeLevelCell || false
      })

      return nonEmpties
    },

    possibleHomeLanguages() {
      var rawHomeLanguageColumn = _map(this.selectedWorksheet, (row) => {
        return row[this.columnMapping.homeLanguage]
      })
      var uniques = _uniq(rawHomeLanguageColumn)
      var nonEmpties = _filter(uniques, (homeLanguageCell) => {
        return homeLanguageCell || false
      })

      return nonEmpties
    },
    rowRejections() {
      if (this.hasHeaderRow) {
        return [0]
      } else {
        return []
      }
    },
    selectedWorksheet() {
      return this.studentDataload.data[this.worksheetIndex]
    },
  },

  methods: {
    getFirstFiveColumnValues(columnIndex) {
      return _map(
        _take(this.selectedWorksheet, 5),
        (row) => {
          return row[columnIndex]
        }
      )
    },

    getColumnMappingValue(mappingKey) {
      return _findKey(this.reverseColumnMapping, (val) => { return val == mappingKey })
    },

    onDone(data) {
      Bus.$emit('flash-message', {text: 'Dataload initiated', type: 'success'})
      this.$router.push({name: 'studentDataloads'})
    },

    onError(error) {
      Bus.$emit('flash-message', {text: error, type: 'error'})
    },

    setGradeLevelMapping(cell, value) {
      if (value === undefined) {
        this.gradeLevelMappings = _omit(this.gradeLevelMappings, [cell])
      } else {
        this.$set(this.gradeLevelMappings, cell, value)
      }
    },

    setHomeLanguageMapping(cell, value) {
      if (value === undefined) {
        this.homeLanguageMappings = _omit(this.homeLanguageMappings, [cell])
      } else {
        this.$set(this.homeLanguageMappings, cell, value)
      }
    },

    setReverseColumnMapping(columnNumber, value) {
      var oldMappedColumn = _findKey(this.reverseColumnMapping, (val) => { return val == value })
      if (oldMappedColumn) {
        this.$set(this.reverseColumnMapping, oldMappedColumn, undefined)
      }
      this.$set(this.reverseColumnMapping, columnNumber, value)
    }
  },

  beforeRouteEnter(to, from, next) {
    preloadMany({
      queries: preloadQueries(to),
      callback: next
    })
  },

  beforeRouteUpdate(to, from, next) {
    preloadMany({
      queries: preloadQueries(to),
      callback: next,
      mountedVm: this
    })
  },

  apollo: {
    studentDataload: {
      query: StudentDataloadQuery,
      variables() {
        return {
          id: this.id
        }
      },
      fetchPolicy: 'cache-first'
    },
    gradeLevels: {
      query: GradeLevelsQuery,
      queryName: 'gradeLevels',
      variables() {
        return { teamId: this.teamId }
      },
      fetchPolicy: 'cache-first'
    },
    homeLanguages: {
      query: HomeLanguagesQuery,
      queryName: "homeLanguages",
      fetchPolicy: 'cache-first'
    }
  }
}
</script>
